<template>
  <div class="policy-rule" :class="{ 'is-disabled': rule.status === 'disabled' }" dir="rtl">
    <!-- Badges row -->
    <div class="rule-badges">
      <span class="badge badge-scope">{{ scopeLabel }}</span>
      <span v-if="confidenceLabel" class="badge" :class="confidenceClass">{{ confidenceLabel }}</span>
      <span v-if="rule.requires_human" class="badge badge-human">דורש בדיקת מגייסת</span>
      <span class="badge badge-source">{{ rule.source === 'manual' ? 'נכתב ידנית' : 'נוצר ע״י AI' }}</span>
      <span class="badge" :class="rule.status === 'active' ? 'badge-active' : 'badge-off'">
        {{ rule.status === 'active' ? 'פעיל' : 'מושבת' }}
      </span>
      <button
        class="status-toggle"
        :disabled="is_saving"
        :title="rule.status === 'active' ? 'השבתת הכלל' : 'הפעלת הכלל'"
        @click="toggleStatus"
      >{{ rule.status === 'active' ? 'השבתה' : 'הפעלה' }}</button>
    </div>

    <!-- Instruction: read mode / inline edit mode -->
    <div v-if="!is_editing" class="rule-instruction" @dblclick="startEdit">
      {{ rule.instruction }}
      <button class="edit-link" title="עריכת ההנחיה" @click="startEdit">עריכה</button>
    </div>
    <div v-else class="rule-edit">
      <textarea
        ref="instruction_input"
        v-model="draft_instruction"
        class="rule-textarea"
        rows="3"
        placeholder="ההנחיה למגייסת"
      ></textarea>
      <textarea
        v-model="draft_reason"
        class="rule-textarea rule-textarea-reason"
        rows="2"
        placeholder="הסיבה לכלל"
      ></textarea>
      <div class="edit-actions">
        <button class="save-btn" :disabled="is_saving || !draft_instruction.trim()" @click="save">
          {{ is_saving ? '...שומר' : 'שמירה' }}
        </button>
        <button class="cancel-btn" :disabled="is_saving" @click="cancelEdit">ביטול</button>
      </div>
    </div>

    <!-- Reason (read mode only — in edit mode it has its own textarea) -->
    <div v-if="!is_editing && rule.reason" class="rule-reason">
      <span class="reason-label">סיבה:</span> {{ rule.reason }}
    </div>

    <!-- Evidence chips -->
    <div v-if="evidence.length" class="rule-evidence">
      <div
        v-for="(item, index) in evidence"
        :key="index"
        class="evidence-chip-wrap"
      >
        <button class="evidence-chip" :title="item.quote || 'ללא ציטוט'" @click="toggleQuote(index)">
          <span class="chip-ref">{{ item.ref || 'ללא מקור' }}</span>
          <span v-if="item.date" class="chip-date">{{ item.date }}</span>
        </button>
        <div v-if="open_quote_index === index" class="evidence-popover">
          <div class="popover-head">
            <router-link
              v-if="offerIdFromRef(item.ref)"
              class="popover-link"
              :to="{ name: 'admin-offers', query: { show_id: offerIdFromRef(item.ref) } }"
            >פתיחת הצעה #{{ offerIdFromRef(item.ref) }}</router-link>
            <span v-else class="popover-ref">{{ item.ref }}</span>
            <button class="popover-close" title="סגירה" @click="open_quote_index = null">×</button>
          </div>
          <div class="popover-quote">{{ item.quote || 'לא נשמר ציטוט לראיה זו' }}</div>
        </div>
      </div>
    </div>

    <div class="rule-meta">
      נוצר ע״י {{ rule.created_by || '—' }}
      <span v-if="rule.updated_by"> · עודכן ע״י {{ rule.updated_by }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'sending-policy-rule-card',
  props: {
    rule: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      is_editing: false,
      is_saving: false,
      draft_instruction: '',
      draft_reason: '',
      open_quote_index: null
    }
  },
  computed: {
    evidence () {
      return Array.isArray(this.rule.evidence) ? this.rule.evidence : []
    },
    scopeLabel () {
      return this.rule.resource_type === 'Position' ? 'משרה' : 'חברה'
    },
    confidenceLabel () {
      if (this.rule.confidence === 'high') return 'ודאות גבוהה'
      if (this.rule.confidence === 'medium') return 'ודאות בינונית'
      return ''
    },
    confidenceClass () {
      return this.rule.confidence === 'high' ? 'badge-high' : 'badge-medium'
    }
  },
  methods: {
    startEdit () {
      this.draft_instruction = this.rule.instruction || ''
      this.draft_reason = this.rule.reason || ''
      this.is_editing = true
      this.$nextTick(() => {
        if (this.$refs.instruction_input) {
          this.$refs.instruction_input.focus()
        }
      })
    },
    cancelEdit () {
      this.is_editing = false
      this.draft_instruction = ''
      this.draft_reason = ''
    },
    save () {
      const instruction = this.draft_instruction.trim()
      if (!instruction || this.is_saving) {
        return
      }
      this.is_saving = true
      this.$network_utils
        .updateSendingPolicy({
          id: this.rule.id,
          instruction: instruction,
          reason: this.draft_reason.trim()
        })
        .then(updated => {
          this.is_editing = false
          this.$utils.emit('show_toast', { text: 'הכלל עודכן' })
          this.$emit('updated', updated)
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בשמירת הכלל — נסי שוב', type: 'error' })
        })
        .finally(() => {
          this.is_saving = false
        })
    },
    toggleStatus () {
      if (this.is_saving) {
        return
      }
      const next_status = this.rule.status === 'active' ? 'disabled' : 'active'
      this.is_saving = true
      this.$network_utils
        .updateSendingPolicy({ id: this.rule.id, status: next_status })
        .then(updated => {
          this.$utils.emit('show_toast', { text: next_status === 'active' ? 'הכלל הופעל' : 'הכלל הושבת' })
          this.$emit('updated', updated)
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בעדכון סטטוס הכלל — נסי שוב', type: 'error' })
        })
        .finally(() => {
          this.is_saving = false
        })
    },
    toggleQuote (index) {
      this.open_quote_index = this.open_quote_index === index ? null : index
    },
    // Only "offer:<id>" refs have an obvious existing screen in v_admin
    // (admin-offers ?show_id=). Every other ref kind stays a quote-only chip.
    offerIdFromRef (ref) {
      const match = /^offer:(\d+)$/.exec(String(ref || '').trim())
      return match ? match[1] : null
    }
  }
}
</script>

<style scoped>
.policy-rule {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
  text-align: right;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.policy-rule.is-disabled {
  background: #f8fafc;
  opacity: 0.75;
}
.rule-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.badge {
  display: inline-block;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 700;
}
.badge-scope {
  background: #e2e3ff;
  color: #3b3f99;
}
.badge-high {
  background: #dcfce7;
  color: #166534;
}
.badge-medium {
  background: #fef3c7;
  color: #92400e;
}
.badge-human {
  background: #fdecea;
  color: #c0392b;
}
.badge-source {
  background: #f1f5f9;
  color: #475569;
}
.badge-active {
  background: #e0f2fe;
  color: #075985;
}
.badge-off {
  background: #e5e7eb;
  color: #4b5563;
}
.status-toggle {
  margin-right: auto;
  background: none;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  color: #475569;
  font-size: 12px;
  padding: 2px 10px;
  cursor: pointer;
}
.status-toggle:hover:not(:disabled) {
  background: #f1f5f9;
}
.rule-instruction {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.5;
}
.edit-link {
  background: none;
  border: none;
  color: #3273dc;
  font-size: 12px;
  cursor: pointer;
  padding: 0 6px;
}
.rule-reason {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}
.reason-label {
  font-weight: 700;
  color: #475569;
}
.rule-textarea {
  width: 100%;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  text-align: right;
  resize: vertical;
}
.rule-textarea-reason {
  margin-top: 6px;
  font-size: 13px;
  color: #4b5563;
}
.edit-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.save-btn {
  background: #25a244;
  border: 1px solid #25a244;
  color: #fff;
  border-radius: 6px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.save-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.cancel-btn {
  background: #fff;
  border: 1px solid #cbd5e0;
  color: #475569;
  border-radius: 6px;
  padding: 6px 18px;
  font-size: 13px;
  cursor: pointer;
}
.rule-evidence {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.evidence-chip-wrap {
  position: relative;
}
.evidence-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 3px 10px;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
}
.evidence-chip:hover {
  background: #e2e8f0;
}
.chip-ref {
  font-weight: 700;
  direction: ltr;
}
.chip-date {
  color: #64748b;
  direction: ltr;
}
.evidence-popover {
  position: absolute;
  z-index: 30;
  top: 26px;
  right: 0;
  width: 300px;
  background: #fff;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  padding: 10px 12px;
}
.popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.popover-link {
  color: #3273dc;
  font-size: 12px;
  text-decoration: underline;
}
.popover-ref {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  direction: ltr;
}
.popover-close {
  background: none;
  border: none;
  font-size: 16px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
}
.popover-quote {
  font-size: 13px;
  color: #1f2937;
  line-height: 1.5;
  white-space: pre-wrap;
}
.rule-meta {
  margin-top: 8px;
  font-size: 11px;
  color: #94a3b8;
}
</style>
